import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiUser,
  FiMail,
  FiPhone,
  FiLock,
  FiCreditCard,
  FiTruck,
  FiUpload,
  FiCheck,
} from 'react-icons/fi';
import { Card } from '../../components/ui';
import driverService from '../../services/driver.service';

const vehicleOptions = [
  { key: 'MOTO', label: 'Moto' },
  { key: 'CARRO', label: 'Carro' },
  { key: 'BICICLETA', label: 'Bicicleta' },
  { key: 'A_PE', label: 'A pe' },
] as const;

interface Props {
  /**
   * 'apply' — conta de estafeta que já existe (sessão iniciada) completa a
   * inscrição: sem dados de conta, só documentos e veículo.
   */
  mode?: 'register' | 'apply';
  onApplied?: () => void;
}

const DriverRegister: React.FC<Props> = ({ mode = 'register', onApplied }) => {
  const isApply = mode === 'apply';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [step, setStep] = useState(isApply ? 2 : 1);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    biNumero: '',
    cartaConducao: '',
    vehicleType: 'MOTO',
    vehiclePlate: '',
    vehicleModel: '',
  });

  const [files, setFiles] = useState<{
    biDoc?: File;
    cartaDoc?: File;
    avatar?: File;
  }>({});

  const setField = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const setFile = (k: 'biDoc' | 'cartaDoc' | 'avatar', f?: File) => setFiles((s) => ({ ...s, [k]: f }));

  const validateStep1 = () => {
    if (!form.name || !form.email || !form.phone || !form.password) {
      setError('Preenche todos os campos');
      return false;
    }
    if (form.password.length < 6) {
      setError('Password deve ter pelo menos 6 caracteres');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      setError('Email invalido');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!form.biNumero) {
      setError('O numero do BI/CNI e obrigatorio');
      return false;
    }
    if (!form.vehicleType) {
      setError('Seleciona o tipo de veiculo');
      return false;
    }
    // Carta só para quem conduz moto ou carro (bicicleta e a pé não precisam)
    if (['MOTO', 'CARRO'].includes(form.vehicleType) && !form.cartaConducao) {
      setError('A carta de conducao e obrigatoria para moto e carro');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    // Fotos dos documentos opcionais: a Pagali pede-as na aprovação, se faltarem
    setError(null);
    setLoading(true);
    try {
      const fd = new FormData();
      const campos = isApply
        ? (['phone', 'biNumero', 'cartaConducao', 'vehicleType', 'vehiclePlate', 'vehicleModel'] as const)
        : (Object.keys(form) as (keyof typeof form)[]);
      campos.forEach((k) => { if (form[k]) fd.append(k, form[k]); });
      if (files.biDoc) fd.append('biDoc', files.biDoc);
      if (files.cartaDoc) fd.append('cartaDoc', files.cartaDoc);
      if (files.avatar) fd.append('avatar', files.avatar);

      if (isApply) {
        await driverService.apply(fd);
        onApplied?.();
        return;
      }
      await driverService.register(fd);
      setDone(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao submeter registo');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4">
            <FiCheck className="text-4xl text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Registo submetido!</h2>
          <p className="text-gray-600 mb-6">
            Recebemos o teu pedido. Vamos rever os teus documentos e vais receber um email quando a conta for aprovada.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 bg-pagali-primary text-white rounded-xl font-medium"
          >
            Ir para login
          </button>
        </Card>
      </div>
    );
  }

  const primeiroPasso = isApply ? 2 : 1;
  const totalPassos = isApply ? 2 : 3;

  return (
    <div className={isApply ? 'p-4' : 'min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4'}>
      <div className="max-w-2xl mx-auto">
        {(!isApply || step > primeiroPasso) && (
        <button
          onClick={() => (step > primeiroPasso ? setStep(step - 1) : navigate('/login'))}
          className="flex items-center gap-2 text-gray-600 mb-4 hover:text-pagali-primary"
        >
          <FiArrowLeft /> Voltar
        </button>
        )}

        <Card className="p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold mb-2">
            {isApply ? 'Completa a tua inscrição de estafeta' : 'Torna-te Pagali Driver'}
          </h1>
          {isApply && (
            <p className="text-gray-600 mb-2">
              Para começares a fazer entregas precisamos dos teus documentos. A equipa Pagali revê e aprova a tua conta.
            </p>
          )}
          <p className="text-gray-500 mb-6">Passo {step - primeiroPasso + 1} de {totalPassos}</p>

          {/* Progress */}
          <div className="flex gap-2 mb-6">
            {(isApply ? [2, 3] : [1, 2, 3]).map((s) => (
              <div
                key={s}
                className={`flex-1 h-2 rounded-full ${s <= step ? 'bg-pagali-primary' : 'bg-gray-200'}`}
              />
            ))}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Dados pessoais */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Dados pessoais</h3>
              <InputField icon={<FiUser />} label="Nome completo" value={form.name} onChange={(v) => setField('name', v)} />
              <InputField icon={<FiMail />} label="Email" type="email" value={form.email} onChange={(v) => setField('email', v)} />
              <InputField icon={<FiPhone />} label="Telefone" type="tel" value={form.phone} onChange={(v) => setField('phone', v)} />
              <InputField icon={<FiLock />} label="Password" type="password" value={form.password} onChange={(v) => setField('password', v)} />
              <button
                onClick={() => validateStep1() && (setError(null), setStep(2))}
                className="w-full py-3 bg-pagali-primary text-white rounded-xl font-medium mt-4"
              >
                Continuar
              </button>
            </div>
          )}

          {/* Step 2: Documentos e veiculo */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Documentos e veiculo</h3>
              {isApply && (
                <InputField icon={<FiPhone />} label="Telefone" type="tel" value={form.phone} onChange={(v) => setField('phone', v)} />
              )}
              <InputField icon={<FiCreditCard />} label="Numero do BI" value={form.biNumero} onChange={(v) => setField('biNumero', v)} />
              <InputField icon={<FiCreditCard />} label={['MOTO', 'CARRO'].includes(form.vehicleType) ? 'Numero da carta de conducao' : 'Numero da carta de conducao (opcional)'} value={form.cartaConducao} onChange={(v) => setField('cartaConducao', v)} />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de veiculo</label>
                <div className="grid grid-cols-2 gap-2">
                  {vehicleOptions.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => setField('vehicleType', v.key)}
                      className={`px-4 py-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                        form.vehicleType === v.key
                          ? 'border-pagali-primary bg-pagali-primary/5 text-pagali-primary'
                          : 'border-gray-200 text-gray-600'
                      }`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              {form.vehicleType !== 'A_PE' && form.vehicleType !== 'BICICLETA' && (
                <>
                  <InputField icon={<FiTruck />} label="Matricula (opcional)" value={form.vehiclePlate} onChange={(v) => setField('vehiclePlate', v)} />
                  <InputField icon={<FiTruck />} label="Modelo (opcional)" value={form.vehicleModel} onChange={(v) => setField('vehicleModel', v)} />
                </>
              )}

              <button
                onClick={() => validateStep2() && (setError(null), setStep(3))}
                className="w-full py-3 bg-pagali-primary text-white rounded-xl font-medium mt-4"
              >
                Continuar
              </button>
            </div>
          )}

          {/* Step 3: Uploads */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Documentos</h3>
              <p className="text-sm text-gray-500">Carrega fotos claras dos documentos. Max 5MB cada.</p>

              <FileUpload
                label="Foto do BI (opcional)"
                file={files.biDoc}
                onChange={(f) => setFile('biDoc', f)}
              />
              <FileUpload
                label="Foto da carta de conducao (opcional)"
                file={files.cartaDoc}
                onChange={(f) => setFile('cartaDoc', f)}
              />
              <FileUpload
                label="Foto de perfil (opcional)"
                file={files.avatar}
                onChange={(f) => setFile('avatar', f)}
                accept="image/*"
              />

              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full py-3 bg-pagali-primary text-white rounded-xl font-medium mt-4 disabled:opacity-50"
              >
                {loading ? 'A submeter...' : 'Submeter registo'}
              </button>
            </div>
          )}
        </Card>

        {!isApply && (
        <p className="text-center mt-4 text-sm text-gray-500">
          Ja tens conta? <Link to="/login" className="text-pagali-primary font-medium">Iniciar sessao</Link>
        </p>
        )}
      </div>
    </div>
  );
};

const InputField: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}> = ({ icon, label, value, onChange, type = 'text' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{icon}</div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-pagali-primary"
      />
    </div>
  </div>
);

const FileUpload: React.FC<{
  label: string;
  file?: File;
  onChange: (f?: File) => void;
  accept?: string;
}> = ({ label, file, onChange, accept = 'image/*,application/pdf' }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <label className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-pagali-primary transition-colors">
      <FiUpload className="text-xl text-gray-400" />
      <span className="flex-1 text-sm text-gray-600">
        {file ? file.name : 'Clica para selecionar'}
      </span>
      <input
        type="file"
        accept={accept}
        onChange={(e) => onChange(e.target.files?.[0])}
        className="hidden"
      />
    </label>
  </div>
);

export default DriverRegister;
